import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ExternalLink, Clock, MapPin } from "lucide-react";

/**
 * Renders an authority name. If the backend has attached `regulator_meta`
 * (typical_processing_days, url, country, notes, name), the name is turned
 * into a hover trigger that opens a small card with those details.
 * Falls back to plain text when no meta is available.
 */
export const RegulatorAuthority = ({ authority, meta }) => {
  const short = authority || "";
  if (!meta) return <span>{short}</span>;

  const url = meta.url || "";
  return (
    <HoverCard openDelay={120} closeDelay={40}>
      <HoverCardTrigger asChild>
        <span
          className="cursor-help underline decoration-dotted underline-offset-2"
          style={{ color: "var(--brand-green)" }}
          data-testid={`regulator-${(meta.name || short).slice(0, 30).replace(/[^a-z0-9]/gi, "-")}`}
        >
          {short}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-80 p-4"
        style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
        data-testid="regulator-hover-card"
      >
        <div className="overline mb-1" style={{ fontSize: "0.6rem" }}>Regulator</div>
        <div className="font-display font-semibold text-[15px] leading-snug" style={{ color: "var(--charcoal)" }}>
          {meta.name || short}
        </div>
        {meta.country && (
          <div className="flex items-center gap-1.5 text-xs mt-1" style={{ color: "var(--muted)" }}>
            <MapPin size={11} /> {meta.country}
          </div>
        )}
        <div className="my-3" style={{ height: 1, background: "var(--hairline)" }} />
        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--body)" }}>
          <Clock size={12} style={{ color: "var(--brand-green)" }} />
          <span>Typical processing:&nbsp;</span>
          <span className="font-mono font-semibold" style={{ color: "var(--charcoal)" }}>
            {meta.typical_processing_days ? `${meta.typical_processing_days} days` : "n/a"}
          </span>
        </div>
        {meta.notes && (
          <p className="text-xs mt-3" style={{ color: "var(--muted)", lineHeight: 1.45 }}>
            {meta.notes}
          </p>
        )}
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium"
            style={{ color: "var(--brand-green)" }}
            data-testid="regulator-hover-card-link"
          >
            <ExternalLink size={12} /> Visit official site
          </a>
        ) : (
          <div className="mt-4 text-xs" style={{ color: "var(--muted)" }}>
            Official contact not yet listed — check ministerial portal.
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
